import React, {
  useContext, useEffect,
  useState
}                                     from 'react'
import PropTypes                      from 'prop-types'
import { Link as RouterLink }         from 'react-router-dom'
import { useTranslation }             from 'react-i18next'
import {
  Box, Button,
  Card, CardContent, CardHeader,
  Chip,
  Container, Divider,
  Grid, Link,
  Stack,
  Typography,
}                                     from '@mui/material'
import { useApi }                     from '../../utils/api'
import { useParams }                  from 'react-router-dom'
import Page                           from '../../components/Page'
import Iconify                        from '../../components/Iconify'
import AlertSimpleDialog              from '../../components/alert-simple-dialog'
import { dateToUserString }           from '../../utils/dateutils'
import useProviders                   from '../../hooks/useProviders'
import { useSnackbar }                from 'notistack'
import BaseTable                      from '../../components/base-table'
import { AppContext }                 from '../../App'
import { MessageDeliveryStatus, MessageFlag, MessageProcessingStatus, User } from '../../consts'

const EMPTY = '---'

const dateText = (date, empty = EMPTY) => date ? dateToUserString(date) : empty


const processingStatusColor = (status) => {
  switch (status) {
    case MessageProcessingStatus.processed: return 'success'
    case null:
    case undefined: return 'info'
    default: return 'error'
  }
}

const deliveryStatusColor = (status) => {
  switch (status) {
    case MessageDeliveryStatus.delivered: return 'success'
    case null:
    case undefined: return 'default'
    default: return 'error'
  }
}

function InfoRow ({ title, children }) {
  return (<Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline', my: 1 }}>
    <Typography variant={'subtitle2'} color={'text.secondary'} sx={{ flex: '0 0 140px', textAlign: 'right', mr: 2 }}>
      {title}
    </Typography>
    <Typography variant={'body1'} component={'div'} sx={{ wordBreak: 'break-word' }}>{children ?? EMPTY}</Typography>
  </Box>)
}

InfoRow.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
}

function InfoCard ({ title, icon, children }) {
  return (<Card>
    <CardHeader
      avatar={<Iconify icon={icon} width={24} height={24} />}
      title={title}
      titleTypographyProps={{ variant: 'subtitle1' }}
      sx={{ pb: 0 }}
    />
    <CardContent sx={{ pt: 1 }}>
      <Divider sx={{ mb: 1 }} />
      {children}
    </CardContent>
  </Card>)
}

InfoCard.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  children: PropTypes.node,
}

export default function MessagePage () {

  const api = useApi()
  const [message, setMessage] = useState({})
  const [service, setService] = useState(null)
  const { messageId } = useParams()
  const [providers] = useProviders()
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { user } = useContext(AppContext)
  const [forceReloadCounter, setForceReloadCounter] = useState(0)
  const [showResendDialog, setShowResendDialog] = useState(false)
  const isAdmin = user.type === User.typeAdmin

  useEffect(() => {
    (async () => {
      const apiClient = user.type === User.typeViewer ? api.viewer : api
      const msg = await apiClient.message.detail(messageId).catch(() => ({}))
      setMessage(msg)
      // service name - service detail is available for admin only
      setService(isAdmin && msg?.serviceId ? await api.service.detail(msg.serviceId).catch(() => null) : null)
    })()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId, forceReloadCounter])

  const logColumns = [
    { field: 'id', headerName: t('ID'), width: 100, sortable: false, filterable: false },
    { field: 'messageId', headerName: t('Message ID'), width: 100, sortable: false, filterable: false, renderCell: (cell) => {
        return cell.row.messageId && <Link
          component={RouterLink}
          variant="body2"
          to={`/dashboard/messages/${cell.row.messageId}`}
        >
          {cell.row.messageId}
        </Link>
      }},
    { field: 'created', headerName: t('Created'), width: 140, sortable: false, filterable: false, renderCell: (cell) => dateToUserString(cell.row.created) },
    { field: 'text', headerName: t('Text'), flex: 1, sortable: false, filterable: false, editable: true, },
    { field: 'rawMessage', headerName: t('Raw message'), flex: 2, sortable: false, filterable: false, editable: true, },
  ];

  const jobColumns = [
    { field: 'id', headerName: t('ID'), width: 80, sortable: false, filterable: false },
    { field: 'created', headerName: t('Created'), width: 140, sortable: false, filterable: false, renderCell: (cell) => dateToUserString(cell.row.created) },
    { field: 'type', headerName: t('Type'), width: 140, sortable: false, filterable: false },
    { field: 'lane', headerName: t('Lane'), width: 150, sortable: false, filterable: false },
    { field: 'status', headerName: t('Status'), width: 90, sortable: false, filterable: false },
    { field: 'runAfter', headerName: t('Run after'), width: 140, sortable: false, filterable: false, renderCell: (cell) => dateToUserString(cell.row.runAfter) },
    { field: 'attempts', headerName: t('Attempts'), width: 80, sortable: false, filterable: false },
    { field: 'lastError', headerName: t('Last error'), flex: 1, sortable: false, filterable: false, editable: true, },
  ];

  const providerText = providers?.find(prov => prov.id === message?.providerId)?.name ?? `${message?.providerId ?? EMPTY}`
  // answer to the message or to the legacy message
  const isOutgoing = !!message.parentId || !!message.answerToLegacyMsg
  const flags = Object.keys(MessageFlag).filter(flag => message[flag])
  const canResend = !!message.id && isAdmin
    && (message.processingStatus === MessageProcessingStatus.failed || message.deliveryStatus === MessageDeliveryStatus.failed)

  const handleCloseResendDialog = (result) => {
    setShowResendDialog(false)
    if (result) {
      api.message.resend(message.id).then(() => {
        setForceReloadCounter(forceReloadCounter + 1)
        enqueueSnackbar(t('Message resent'), { variant: 'success' })
      }).catch(e => {
        console.log(e)
        enqueueSnackbar(t('Failed to resent message'), { variant: 'error' })
      })
    }
  }

  const tableLoadFailed = (what, e) => {
    console.log(`Failed to load message ${what}`, e)
    return {
      data: [],
      totalCount: 0,
    }
  }

  return (
    <Page title={t('Message')}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>

        {/* header: direction, statuses, actions */}
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
          <Typography component="h1" variant="h3">
            {t('message_id', { id: messageId })}
          </Typography>
          {!!message.id && <Stack direction={'row'} spacing={1} flexWrap={'wrap'}>
            <Chip
              icon={<Iconify icon={isOutgoing ? 'material-symbols:call-made' : 'material-symbols:call-received'} />}
              label={isOutgoing ? t('Outgoing') : t('Incoming')}
              variant={'outlined'}
            />
            <Chip
              label={`${t('Processing status')}: ${t(message.processingStatus ?? MessageProcessingStatus.scheduled)}`}
              color={processingStatusColor(message.processingStatus)}
            />
            <Chip
              label={`${t('Delivery status')}: ${message.deliveryStatus ? t(message.deliveryStatus) : EMPTY}`}
              color={deliveryStatusColor(message.deliveryStatus)}
              variant={message.deliveryStatus ? 'filled' : 'outlined'}
            />
          </Stack>}
          <Box flexGrow={1} />
          {canResend &&
            <Button variant={'contained'} startIcon={<Iconify icon={'material-symbols:refresh'} />} onClick={() => setShowResendDialog(true)}>
              {t('Resend')}
            </Button>
          }
        </Stack>

        {!!message.id && <Grid container spacing={2}>
          {/* left column: what was sent and for how much */}
          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              <InfoCard title={t('Content')} icon={'material-symbols:sms-outline'}>
                <InfoRow title={t('Tel. from')}>{message.telFrom}</InfoRow>
                <InfoRow title={t('Tel. to')}>{message.telTo}</InfoRow>
                <InfoRow title={t('Operator')}>{message.operator}</InfoRow>
                <Box sx={{ mt: 2, p: 2, borderRadius: 1, bgcolor: 'background.neutral', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  <Typography variant="body1">{message.content || EMPTY}</Typography>
                </Box>
              </InfoCard>

              <InfoCard title={t('Price')} icon={'material-symbols:payments-outline'}>
                <InfoRow title={t('Price')}>{message.priceWithVat}</InfoRow>
                <InfoRow title={t('PriceNoVat')}>{message.price}</InfoRow>
                <InfoRow title={t('Vat')}>{message.vat}</InfoRow>
                <InfoRow title={t('VAT rate')}>{message.vatRate}</InfoRow>
                {isAdmin && <InfoRow title={t('Billing code')}>{message.billingCodeId}</InfoRow>}
              </InfoCard>
            </Stack>
          </Grid>

          {/* right column: processing - status, time, where it belongs */}
          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              <InfoCard title={t('Status')} icon={'material-symbols:sync'}>
                <InfoRow title={t('Processing status')}>{t(message.processingStatus ?? MessageProcessingStatus.scheduled)}</InfoRow>
                <InfoRow title={t('Delivery status')}>{message.deliveryStatus ? t(message.deliveryStatus) : EMPTY}</InfoRow>
                <InfoRow title={t('Status changed')}>{dateText(message.statusChanged)}</InfoRow>
                <InfoRow title={t('Attempts')}>{`${message.attempts ?? 0} x`}</InfoRow>
                <InfoRow title={t('Flags')}>
                  {flags.length > 0 ? <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {flags.map(flag => <Chip key={flag} size={'small'} variant={'outlined'} label={t(MessageFlag[flag])} />)}
                  </Box> : null}
                </InfoRow>
              </InfoCard>

              <InfoCard title={t('Dates')} icon={'material-symbols:schedule-outline'}>
                <InfoRow title={t('Created')}>{dateText(message.created)}</InfoRow>
                <InfoRow title={t('Received')}>{dateText(message.received)}</InfoRow>
                <InfoRow title={t('Scheduled')}>{dateText(message.scheduled, t('Immediately'))}</InfoRow>
                <InfoRow title={t('Expired')}>{dateText(message.expired)}</InfoRow>
                <InfoRow title={t('Modified')}>{dateText(message.modified)}</InfoRow>
              </InfoCard>

              <InfoCard title={t('Identification')} icon={'material-symbols:link'}>
                <InfoRow title={t('ID')}>{message.id}</InfoRow>
                <InfoRow title={t('Foreign ID')}>{message.foreignId}</InfoRow>
                <InfoRow title={t('UUID')}>{message.uuid}</InfoRow>
                <InfoRow title={t('Parent SMS')}>
                  {message.parentId && <Link component={RouterLink} to={`/dashboard/messages/${message.parentId}`}>{message.parentId}</Link>}
                </InfoRow>
                <InfoRow title={t('Service')}>
                  {message.serviceId && (isAdmin
                    ? <Link component={RouterLink} to={`/dashboard/services/${message.serviceId}`}>{service?.name ?? message.serviceId}</Link>
                    : message.serviceId)}
                </InfoRow>
                {isAdmin && <InfoRow title={t('Provider')}>{providerText}</InfoRow>}
              </InfoCard>
            </Stack>
          </Grid>

          {isAdmin &&
            <Grid item xs={12}>
              <Typography variant={'h4'} sx={{ mt: 2, mb: 1 }}>{t('Jobs')}</Typography>
              <BaseTable
                columns={jobColumns}
                autoHeight={true}
                forceReloadCounter={forceReloadCounter}
                loadCallback={async () => {
                  try {
                    return await api.message.jobs(messageId)
                  } catch (e) {
                    return tableLoadFailed('jobs', e)
                  }
                }}
              />
            </Grid>
          }
          <Grid item xs={12}>
            <Typography variant={'h4'} sx={{ mt: 2, mb: 1 }}>{t('Logs')}</Typography>
            <BaseTable
              columns={logColumns}
              autoHeight={true}
              forceReloadCounter={forceReloadCounter}
              loadCallback={async () => {
                try {
                  return await (user.type === User.typeViewer ? api.viewer.messageLog : api.messageLog).listForMessage(messageId)
                } catch (e) {
                  return tableLoadFailed('logs', e)
                }
              }}
            />
          </Grid>
        </Grid>}
        <AlertSimpleDialog
          title={t('Resend')}
          message={t('resend-confirm-msg', { msgId: message.id })}
          open={showResendDialog}
          onClose={handleCloseResendDialog}
        />
      </Container>
    </Page>
  )

}
